import React, { Suspense, useMemo, lazy, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { createUseStyles } from 'react-jss';
import {
  Theme,
  Button,
  icons,
  SideDropArrowIcon,
  DryIcon,
  RefrigeratedIcon,
  FrozenIcon,
  PickupIcon,
  DeliveryIcon,
  ActiveLaneIcon,
  ColorBadge,
  AlertService,
  Timing,
  Loading,
  MetricCard,
  OverlayService,
  SpoilerAlertAppleIcon,
  SparkleGradientIcon,
  DollarCircleIcon,
  ActiveLaneTruckIcon,
  InactiveLaneTruckIcon,
  DeliveryTruckIcon,
  SnowflakeIcon,
  DryBoxIcon,
  RefrigeratorIcon,
  MiniTooltip,
} from '@spoiler-alert/ui-library';
import accounting from 'accounting';
import { useMutation, useQuery } from '@apollo/client';
import safeGet from '../../utilities/safe-get';
import { initialDrilldownMetrics, generateMetrics, TrucklaneActionInProgressContext } from './trucklane-utility';
import { offerlistingStatuses, subscriptionTier } from '../../enums';
import { OfferListingsByInventoryQuery, OfferListingsByTrucklaneQuery, trucklanePollingQuery, trucklaneExceptionsQuery } from '../../graphql/queries';
import { IgnoreOfferListings, RestoreOfferListings } from '../../graphql/mutations';
import { Store } from '../../store';
import RecalculationBanner from './recalculation-banner';
import TrucklaneDrilldownTables from './trucklane-drilldown-tables';
import Tracking from '../../utilities/tracking-apis';
import OfferShiftContext from '../offer-comparison/offer-shift-context';
import { ACTIVE_STEP } from '../offer-comparison/active-step-enums';
import { checkFeature } from '../../helpers';
import { featureFlags } from '../../enums';
import DisplayBuyerMinimums from './buyer-minimum-display';

const TruckRouteMap = lazy(() => import('../../components/truck-route-map/truck-route-map'));

const styles = {
  drilldownContainer: {
    overflowY: 'auto',
    padding: '0px 24px',
  },
  rowContainer: {
    display: 'flex',
    marginTop: 24,
    marginBottom: 36,
  },
  row: {
    display: 'flex',
  },
  wrapRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 8,
    flexWrap: 'wrap',
  },
  rowClose: {
    display: 'flex',
  },
  rowBreak: {
    marginTop: '12px',
  },
  flexGrow: {
    containerType: 'inline-size',
    flex: 1,
    marginRight: 24,
  },
  cardOverview: {
    display: 'flex',
    flexDirection: 'column',
    padding: '24px 0px',
    '& h1': {
      fontSize: '14px',
      fontWeight: '500',
      margin: 0,
    },
    '& svg': {
      marginRight: '8px',
    },
    '& h2': {
      fontSize: '18px',
      fontWeight: 'normal',
      margin: 0,
    },
  },
  overviewLogistics: {
    display: 'flex',
    alignItems: 'center',
    '& p': {
      margin: 0,
      fontSize: '16px',
      color: Theme.grey,
      fontWeight: 'normal',
      marginRight: 4,
    },
  },
  activeLane: {
    border: `1px solid ${Theme.primaryColor}`,
    backgroundColor: Theme.green5,
    borderRadius: '2px',
    marginRight: 12,
    display: 'flex',
    alignItems: 'center',
    '& p': {
      color: Theme.primaryColor,
      width: 'fit-content',
      padding: '2px 8px 2px 0px',
      marginBottom: 0,
      fontSize: '0.75rem',
      fontWeight: 500,
      lineHeight: '0.75rem',
      whiteSpace: 'nowrap',
    },
    '& svg': {
      stroke: Theme.primaryColor,
      fill: 'transparent',
      width: 20,
      height: 20,
      marginLeft: 8,
    },
  },
  logisticsIcon: {
    height: 16,
    width: 16,
    fill: Theme.grey,
  },
  overviewActions: {
    marginTop: 24,
    display: 'flex',
    '& button': {
      marginRight: 8,
    },
  },
  buyerNameContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: 4,
    justifyContent: 'space-between',
  },
  spoilerAlertApple: {
    height: 18,
    width: 16,
  },
  truckData: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    margin: '24px 0px',
    fontSize: 16,
    fontWeight: '400',
    gap: 12,
    '& svg': {
      margin: 0,
    },
  },
  truckDataBox: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    letterSpacing: '-0.176px',
  },
  trucklaneBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: 14,
    fontWeight: '500',
    lineHeight: '16px',
    '& svg': {
      margingRight: 6,
    },
  },
  activeTruckLane: {
    extend: 'trucklaneBadge',
    color: Theme.primaryColor,
  },
};
const useStyles = createUseStyles(styles);

const TrucklaneDrilldown = ({
  user,
  trucklaneId,
  offers,
  recalculation,
  handleDrilldownRecalculation,
  loading,
  staleTab,
  history,
  activeStep,
  buyerMinimums,
}) => {
  const reservePriceExceptionsEnabled = checkFeature(featureFlags.reservePriceExceptions);
  const aggregateOffer = offers[0];
  const classes = useStyles();
  const { actionInProgress, setActionInProgress } = useContext(TrucklaneActionInProgressContext);
  const saasLiteCustomer = user.site.subscriptionTier === subscriptionTier.saasLite;
  // Quoted as a rate for one truck on every tab; Truck Count carries how many trucks the lane needs.
  const perTruckCost = aggregateOffer?.trucklaneCost || 0;
  const [ignoreOffersMutation, { loading: ignoreOffersLoading }] = useMutation(IgnoreOfferListings);
  const [restoreOffersMutation, { loading: restoreOffersLoading }] = useMutation(RestoreOfferListings);
  const { data, loading: trucklaneQueryLoading } = useQuery(OfferListingsByTrucklaneQuery, {
    variables: {
      buyerSiteId: aggregateOffer?.buyerSiteId || '',
      truckType: aggregateOffer?.truckType || '',
      logisticsTerm: aggregateOffer?.logisticsTerm || '',
      transactionLocationId: aggregateOffer?.transactionLocationId || '',
      sellerLocationId: aggregateOffer?.sellerLocationId || '',
      buyerDestinationId: aggregateOffer?.buyerDestinationId || '',
    },
    fetchPolicy: 'network-only',
  });
  const { offerShiftsMappedToTrucklanes = new Map() } = useContext(OfferShiftContext) || {};

  const logisticsIcons = {
    Pickup: PickupIcon,
    Delivery: DeliveryIcon,
  };
  const logisticsIconsV2 = {
    Pickup: PickupIcon,
    Delivery: DeliveryTruckIcon,
  };
  const truckTypeIcons = {
    Dry: DryIcon,
    Refrigerated: RefrigeratedIcon,
    Frozen: FrozenIcon,
  };
  const truckTypeIconsV2 = {
    Dry: DryBoxIcon,
    Refrigerated: RefrigeratorIcon,
    Frozen: SnowflakeIcon,
  };
  const LogisticsIcon = reservePriceExceptionsEnabled
    ? logisticsIconsV2[aggregateOffer?.logisticsTerm]
    : logisticsIcons[aggregateOffer?.logisticsTerm];
  const TruckIcon = reservePriceExceptionsEnabled ? truckTypeIconsV2[aggregateOffer?.truckType] : truckTypeIcons[aggregateOffer?.truckType];
  const invalidTrucklanesExist = !!Store.invalidTrucklaneChannels.size;

  useEffect(() => {
    OverlayService.show();
    return () => {
      OverlayService.hide();
    };
  }, []);

  const metrics = useMemo(() => {
    // Which metrics exist depends only on the site's settings, which are available immediately — so build the
    // list before the data check. Deciding it inside meant the placeholder cards rendered without Truck Count
    // and it popped in once the lane query resolved, shifting the row.
    const truckMaximumsSet = user?.site?.truckMaximums?.mode !== 'none' && (user?.site?.truckMaximums?.weight || user?.site?.truckMaximums?.pallets);
    // Splice into a local copy — mutating the shared imported array would duplicate Truck Count across
    // re-renders and leak it into sites that have no maximums configured.
    const drilldownMetrics = [...initialDrilldownMetrics];
    // Anchor on Pallets rather than a fixed index — the metric list has changed shape before and a stale
    // index silently drops these in the wrong slot. Both only apply when a maximum is configured; with mode
    // none there is no truck count to show and no multiplier to make a lane cost meaningful.
    if (truckMaximumsSet)
      drilldownMetrics.splice(
        drilldownMetrics.findIndex((metric) => metric.title === 'Pallets') + 1,
        0,
        { title: 'Truck Count', current: '—', projected: '-', projectedExceptions: '—' },
        { title: 'Lane Cost', current: '—', projected: '-', projectedExceptions: '—' },
      );

    if (data?.offerListingsByTrucklaneQuery?.data?.length) {
      const offerData = data.offerListingsByTrucklaneQuery.data.map((trucklane) => {
        return { ...trucklane.offerListings[0], inventory: trucklane.inventory };
      });
      const shifts = offerShiftsMappedToTrucklanes.get(trucklaneId);
      const drillMap = new Map();
      if (shifts) drillMap.set('drilldown', shifts);
      return generateMetrics(user, [{ id: 'drilldown', offers: offerData }], drilldownMetrics, recalculation, drillMap, [
        { id: 'drilldown', offers: offerData },
      ]);
    }
    return drilldownMetrics;
  }, [recalculation, data, offerShiftsMappedToTrucklanes, trucklaneId, user]);

  const { awardedInventory, fillupInventory, suggestedInventory, acceptedInventory, ignoredInventory, rpeInventory } = useMemo(() => {
    const awardedInv = [];
    const fillupInv = [];
    const acceptedInv = [];
    const suggestedInv = [];
    const ignoredInv = [];
    const rpeInv = [];
    const reconfigedData = (data?.offerListingsByTrucklaneQuery?.data || []).reduce((newSet, row) => {
      row.offerListings.forEach((ol) => {
        if (ol.buyerSiteId === aggregateOffer?.buyerSiteId)
          newSet.push({
            ...row.inventory,
            offerListing: ol,
            competingOffers: row.competingOffers,
            id: row.inventory._id,
            _id: `${row.inventory._id}_${ol._id}`,
          });
      });
      return newSet;
    }, []);
    if (offerShiftsMappedToTrucklanes.has(trucklaneId)) {
      const shifts = offerShiftsMappedToTrucklanes.get(trucklaneId);
      shifts.forEach((shift) => {
        reconfigedData.push({
          ...shift.shiftInventory,
          shift: shift,
          offerListing: {
            quantity: shift.shiftedQuantity,
            totalPrice: shift.shiftedQuantity * shift.unitPrice,
            status: offerlistingStatuses.ACTIVE,
            suggestions: {
              award: {
                suggested: false,
                quantity: shift.shiftedQuantity,
              },
            },
          },
          competingOffers: 0,
          id: shift.shiftInventory._id,
          _id: shift._id,
        });
      });
    }
    reconfigedData.forEach((row) => {
      if (row.offerListing.status === offerlistingStatuses.IGNORED) {
        ignoredInv.push(row);
      } else if (row.offerListing.status === offerlistingStatuses.AWARDED) {
        awardedInv.push(row);
      } else if (row.offerListing.status === offerlistingStatuses.ACCEPTED) {
        acceptedInv.push(row);
      } else if (row.offerListing.suggestions?.award?.suggested || row.shift) {
        suggestedInv.push(row);
      } else if (
        reservePriceExceptionsEnabled &&
        row.offerListing.suggestions?.reservePriceException?.suggested &&
        activeStep === ACTIVE_STEP.OPPORTUNITIES
      ) {
        rpeInv.push(row);
      } else {
        fillupInv.push(row);
      }
    });

    return {
      awardedInventory: awardedInv,
      fillupInventory: fillupInv.filter((inv) => !awardedInv.map((awardInv) => awardInv.id).includes(inv.id)),
      acceptedInventory: acceptedInv,
      suggestedInventory: recalculation ? [] : suggestedInv,
      ignoredInventory: ignoredInv,
      rpeInventory: rpeInv,
    };
  }, [data, recalculation, aggregateOffer, offerShiftsMappedToTrucklanes, trucklaneId, reservePriceExceptionsEnabled]);

  const { activeTruckLane, ignoredOffers } = useMemo(() => {
    const active = offers.filter((offer) => [offerlistingStatuses.AWARDED, offerlistingStatuses.ACCEPTED].includes(offer.status));
    const ignored = offers.filter((offer) => [offerlistingStatuses.IGNORED].includes(offer.status));
    return { activeTruckLane: active, ignoredOffers: ignored };
  }, [offers]);

  const displayLogisticsTerm = () => {
    return aggregateOffer?.logisticsTerm === 'Pickup' ? (
      <div className={classes.overviewLogistics}>
        {LogisticsIcon && <LogisticsIcon className={classes.logisticsIcon} />}
        <p>{aggregateOffer?.logisticsTerm}</p>
      </div>
    ) : (
      <div className={classes.overviewLogistics}>
        {LogisticsIcon && <LogisticsIcon className={classes.logisticsIcon} />}
        <p>
          {aggregateOffer?.logisticsTerm || '-'} - {accounting.formatNumber(aggregateOffer?.trucklaneDistance || 0)} miles
        </p>
        {!saasLiteCustomer && <p>{accounting.formatMoney(perTruckCost)} Est. Cost / Truck</p>}
      </div>
    );
  };

  const ignoreOffers = Timing.throttle(() => {
    setActionInProgress(true);
    const matchedOffers = [
      ...fillupInventory.filter((inv) => inv.offerListing.status === offerlistingStatuses.ACTIVE),
      ...(activeStep === ACTIVE_STEP.OPPORTUNITIES ? rpeInventory : suggestedInventory).filter(
        (inv) => inv.offerListing.status === offerlistingStatuses.ACTIVE,
      ),
    ];
    ignoreOffersMutation({
      variables: { offerListingIds: matchedOffers.map((inv) => inv.offerListing._id) },
      refetchQueries: [
        {
          query: trucklanePollingQuery,
          variables: {
            pollingDetails: [...Store.trucklaneChannels.values()].map((channel) => ({ origin: channel.originDCId, truckType: channel.truckType })),
          },
        },
        {
          query: OfferListingsByInventoryQuery,
          variables: { inventoryIds: matchedOffers.map((inv) => inv.id) },
        },
        {
          query: OfferListingsByTrucklaneQuery,
          variables: {
            buyerSiteId: aggregateOffer?.buyerSiteId || '',
            truckType: aggregateOffer?.truckType || '',
            logisticsTerm: aggregateOffer?.logisticsTerm || '',
            transactionLocationId: aggregateOffer?.transactionLocationId || '',
            sellerLocationId: aggregateOffer?.sellerLocationId || '',
            buyerDestinationId: aggregateOffer?.buyerDestinationId || '',
          },
        },
      ],
    }).then((ignoreResp) => {
      if (ignoreResp.data?.ignoreOfferListingsById?.errors?.length)
        AlertService.alert({
          type: 'warning',
          autoDismiss: true,
          message: <span>{ignoreResp.data?.ignoreOfferListingsById?.errors[0].message}</span>,
        });
      Tracking.Event('Offers were ignored in trucklane drilldown', {
        userId: user._id,
      });
      setActionInProgress(false);
    });
  }, 1000);

  const restoreOffers = Timing.throttle(() => {
    setActionInProgress(true);
    const matchedOffers = ignoredInventory.filter((inv) => inv.offerListing.status === offerlistingStatuses.IGNORED);
    restoreOffersMutation({
      variables: { offerListingIds: matchedOffers.map((inv) => inv.offerListing._id) },
      refetchQueries: [
        {
          query: trucklanePollingQuery,
          variables: {
            pollingDetails: [...Store.trucklaneChannels.values()].map((channel) => ({ origin: channel.originDCId, truckType: channel.truckType })),
          },
        },
        {
          query: OfferListingsByInventoryQuery,
          variables: { inventoryIds: matchedOffers.map((inv) => inv.id) },
        },
        {
          query: OfferListingsByTrucklaneQuery,
          variables: {
            buyerSiteId: aggregateOffer?.buyerSiteId || '',
            truckType: aggregateOffer?.truckType || '',
            logisticsTerm: aggregateOffer?.logisticsTerm || '',
            transactionLocationId: aggregateOffer?.transactionLocationId || '',
            sellerLocationId: aggregateOffer?.sellerLocationId || '',
            buyerDestinationId: aggregateOffer?.buyerDestinationId || '',
          },
        },
      ],
    }).then((restoreResp) => {
      if (restoreResp.data?.restoreOfferListingsById?.errors?.length)
        AlertService.alert({
          type: 'warning',
          autoDismiss: true,
          message: <span>{restoreResp.data?.restoreOfferListingsById?.errors[0].message}</span>,
        });
      Tracking.Event('Offers were restored in trucklane drilldown', {
        userId: user._id,
      });
      setActionInProgress(false);
    });
  }, 1000);

  const displayTruckDataInRow = () => {
    return (
      <div className={`${classes.truckData}`}>
        <div className={classes.truckDataBox}>
          {TruckIcon && <TruckIcon className={classes.logisticsIcon} />}
          <span data-testid={'card-truck-type'}>{aggregateOffer?.truckType}</span>
        </div>
        {aggregateOffer?.logisticsTerm === 'Pickup' ? (
          <div className={classes.truckDataBox}>
            {LogisticsIcon && <LogisticsIcon className={classes.logisticsIcon} />}
            <span>{aggregateOffer?.logisticsTerm}</span>
            {
              <DisplayBuyerMinimums
                buyerMinimums={buyerMinimums}
                trucklaneMinimums={user?.site?.trucklaneMinimums}
                aggregateOffer={aggregateOffer}
                reservePriceExceptionsEnabled={reservePriceExceptionsEnabled}
              />
            }
          </div>
        ) : (
          <>
            <div className={classes.truckDataBox}>
              {LogisticsIcon && <LogisticsIcon className={classes.logisticsIcon} />}
              <span>
                {aggregateOffer?.logisticsTerm} - {accounting.formatNumber(aggregateOffer?.trucklaneDistance)} miles
              </span>
            </div>
            {!saasLiteCustomer && (
              <span className={classes.truckDataBox}>
                <DollarCircleIcon /> {accounting.formatMoney(perTruckCost)} Est. Cost / Truck
              </span>
            )}
          </>
        )}
      </div>
    );
  };

  const getIgnoreButton = () => {
    if (!reservePriceExceptionsEnabled) return null;
    const inRestoreMode = ignoredOffers.length > 0;
    const allInActiveLane = offers.length > 0 && activeTruckLane.length === offers.length;
    const ignorableBucket =
      activeStep === ACTIVE_STEP.OPPORTUNITIES ? [...rpeInventory, ...fillupInventory] : [...suggestedInventory, ...fillupInventory];
    const hasIgnorableOffers = ignorableBucket.some((inv) => inv.offerListing.status === offerlistingStatuses.ACTIVE);
    const disabled = ignoreOffersLoading || restoreOffersLoading || actionInProgress || allInActiveLane || (!inRestoreMode && !hasIgnorableOffers);
    return (
      <MiniTooltip text={inRestoreMode ? 'Restore Offers' : 'Ignore Offers'}>
        <Button
          disabled={disabled}
          loadingText=""
          loading={ignoreOffersLoading || restoreOffersLoading}
          onClick={(e) => (inRestoreMode ? restoreOffers(e) : ignoreOffers(e))}
          icon={inRestoreMode ? icons.restore : icons.ignore}
          secondary
        />
      </MiniTooltip>
    );
  };

  const getLaneStatus = () => {
    return activeTruckLane.length > 0 ? (
      <div className={classes.activeTruckLane}>
        <ActiveLaneTruckIcon /> <span>ACTIVE LANE</span>
      </div>
    ) : (
      <div className={classes.trucklaneBadge}>
        <InactiveLaneTruckIcon /> <span>INACTIVE LANE</span>
      </div>
    );
  };

  if (!aggregateOffer) {
    return (
      <div className={classes.drilldownContainer}>
        <Loading loading={true} />
      </div>
    );
  }

  return (
    <div className={classes.drilldownContainer}>
      <div className={classes.rowContainer}>
        <div className={classes.flexGrow}>
          <div className={classes.cardOverview}>
            <div className={classes.buyerNameContainer}>
              <div>
                <h1>{aggregateOffer?.buyerSiteName}</h1>
                {aggregateOffer?.isVendorOfRecord && <SpoilerAlertAppleIcon className={classes.spoilerAlertApple} />}
              </div>
              {reservePriceExceptionsEnabled && getIgnoreButton()}
            </div>
            <h2>{aggregateOffer?.inventory?.siteName}</h2>
            <div className={classes.rowClose}>
              <SideDropArrowIcon />
              <h2>{aggregateOffer?.buyerDestinationName}</h2>
            </div>
            {reservePriceExceptionsEnabled ? (
              displayTruckDataInRow()
            ) : (
              <>
                <div className={`${classes.overviewLogistics} ${classes.rowBreak}`}>
                  <TruckIcon className={classes.logisticsIcon} />
                  <p data-testid={'card-truck-type'}>{aggregateOffer?.truckType}</p>
                </div>
                <div className={classes.row}>
                  {displayLogisticsTerm()}
                  {
                    <DisplayBuyerMinimums
                      buyerMinimums={buyerMinimums}
                      trucklaneMinimums={user?.site?.trucklaneMinimums}
                      aggregateOffer={aggregateOffer}
                      reservePriceExceptionsEnabled={reservePriceExceptionsEnabled}
                    />
                  }
                </div>
              </>
            )}

            <div className={`${classes.row} ${classes.rowBreak}`}>
              {!reservePriceExceptionsEnabled && activeTruckLane?.length > 0 && (
                <div className={classes.activeLane}>
                  <ActiveLaneIcon /> <p>ACTIVE TRUCK LANE</p>
                </div>
              )}
              {reservePriceExceptionsEnabled && getLaneStatus()}
              {ignoredOffers.length > 0 && !reservePriceExceptionsEnabled && <ColorBadge theme={'grey'}>IGNORED OFFERS</ColorBadge>}
            </div>
          </div>
          <div className={classes.wrapRow}>
            {metrics.map((metric, index) => (
              <MetricCard
                key={index}
                title={metric.title}
                current={metric.current}
                projected={activeStep === ACTIVE_STEP.OPPORTUNITIES ? metric.projectedExceptions : metric.projected}
                projectedIcon={<SparkleGradientIcon />}
                loading={loading || trucklaneQueryLoading}
              />
            ))}
          </div>
          {!reservePriceExceptionsEnabled && (
            <div className={classes.overviewActions}>
              <Button
                loadingText="Ignoring Offers"
                icon={icons.ignore}
                secondary
                onClick={ignoreOffers}
                loading={ignoreOffersLoading}
                disabled={ignoreOffersLoading || (!suggestedInventory.length && !fillupInventory.length)}
              >
                Ignore Offers
              </Button>
              <Button
                loadingText="Restoring Offers"
                icon={icons.restore}
                secondary
                onClick={restoreOffers}
                loading={restoreOffersLoading}
                disabled={restoreOffersLoading || !ignoredInventory.length}
              >
                Restore Offers
              </Button>
            </div>
          )}
        </div>

        <Suspense fallback={<Loading loading={true} />}>
          <TruckRouteMap
            logisticsTerm={aggregateOffer?.logisticsTerm}
            origin={{
              lat: safeGet(data, 'offerListingsByTrucklaneQuery.data.0.originLocation.latitude', 0),
              long: safeGet(data, 'offerListingsByTrucklaneQuery.data.0.originLocation.longitude', 0),
            }}
            destination={{
              lat: safeGet(data, 'offerListingsByTrucklaneQuery.data.0.destinationLocation.latitude', 0),
              long: safeGet(data, 'offerListingsByTrucklaneQuery.data.0.destinationLocation.longitude', 0),
            }}
            trucklaneId={trucklaneId}
            loading={trucklaneQueryLoading}
          />
        </Suspense>
      </div>
      <TrucklaneDrilldownTables
        user={user}
        history={history}
        recalculation={recalculation}
        trucklaneId={trucklaneId}
        loading={loading || trucklaneQueryLoading}
        awardedInventory={awardedInventory}
        fillupInventory={fillupInventory}
        suggestedInventory={suggestedInventory}
        rpeInventory={rpeInventory}
        acceptedInventory={acceptedInventory}
        aggregateOffer={aggregateOffer}
        activeStep={activeStep}
        buyerMinimums={buyerMinimums}
      />
      {invalidTrucklanesExist && !actionInProgress && !loading && !staleTab && (
        <RecalculationBanner recalculateAction={() => handleDrilldownRecalculation(trucklaneId)} />
      )}
    </div>
  );
};

TrucklaneDrilldown.propTypes = {
  user: PropTypes.object,
  history: PropTypes.object,
  trucklaneId: PropTypes.string,
  offers: PropTypes.arrayOf(PropTypes.object),
  recalculation: PropTypes.bool,
  handleDrilldownRecalculation: PropTypes.func,
  loading: PropTypes.bool,
  staleTab: PropTypes.bool,
  activeStep: PropTypes.string,
  buyerMinimums: PropTypes.object,
};

export default TrucklaneDrilldown;
